/* eslint-disable camelcase */
/* eslint-disable react-perf/jsx-no-new-object-as-prop */
import React, { useCallback, useMemo, useState } from 'react'
import {
  Anchor,
  Box,
  IcoMoon,
  Text,
  ArrowDownIcon,
} from '@usap/uicomponents'
import { outsideClickHandler } from '@usap/core'
import commaSeparetedValue from '../../../helpers/commaSeparetedValue'
import SetKitDisplay from '../SetKitDisplay'
import ReturnStatus from '../ReturnStatus'
import creditStatusMapping from '../RMA/Mapping/creditStatusMapping'
import returnRequestStatusMapping from '../RMA/Mapping/returnRequestStatusMapping'
import OrderDetailItemButton from './button/OrderDetailItemButton'
import { getButtonType } from '../OrderHistory/components/buttons/manageButtonType'
import useGetLifetimeReplacementFlag from '../RMA/hooks/useGetLifetimeReplacementFlag'
import { CorePriceBadge, CoreInfoUpdated } from '../../product'

import ProductImage from './Product/Image'
import ProductInformation from './Product/Information'

import {
  Container,
  ResponsiveContainer,
  OrderItemBox,
  OrderDetailsBox,
  OrderDetailsTextBox,
  ActionContainer,
  ActionTrackContainer,
  LiveChat,
  KitContainer,
  CorePriceContainer,
  CorePriceTooltip,
  CorePriceTooltipContent,
  CorePriceHelpText,
  CorePriceTooltipArrow,
  OrderDetailsBlueButton,
} from './OrderItem.css'
import getTitleAndMessage from './hooks/getTitleAndMessage'
import BuyItAgainButton from './button/BuyItAgainButton'

const ToggleCore = ({ product, coreConfig }) => {
  const [showInformation, shouldShowInformation] = useState(false)
  const hasCorePrice = Boolean(product.itemTotalCore) && Number(product.itemTotalCore) !== 0
  const corePrice = commaSeparetedValue(product.itemTotalCore)
  const corePriceEach = product.qty > 1 ? ` ($${commaSeparetedValue(product.corePrice)} each)` : ''

  const handleShowInformation = useCallback(
    () => {
      outsideClickHandler(
        showInformation,
        [`iconClick${product.oid}`],
        () => {
          shouldShowInformation(false)
        },
      )
      shouldShowInformation(!showInformation)
    },
    [product.oid, shouldShowInformation, showInformation],
  )

  return hasCorePrice && (
    <CorePriceContainer>
      {showInformation && (
        <CoreInfoUpdated forAccount config={coreConfig} />
      )}

      <Text size="small" color="dark-2" truncate>
        {`Core Charge: $${corePrice}${corePriceEach}`}
      </Text>
      <CorePriceBadge forAccount uniqueId={product.oid} onClick={handleShowInformation} showCorePrice={showInformation} />
    </CorePriceContainer>
  )
}

export default function OrderItem({
  product,
  config,
  isDesktop,
  orderDetails,
  itemDetails,
  setReturnDetailsData,
  setDisplayReturnDetails,
  navigateToRMA,
  creditStatus,
  returnRequestStatus,
  orderId,
  formatBrand,
  OpenChatHandler,
  coreConfig,
}) {
  const vehicle = useMemo(() => (product && product.vehicle) || {}, [product])

  const core = useMemo(
    () => (<ToggleCore product={product} coreConfig={coreConfig} />),
    [product, coreConfig],
  )
  const itemStatus = product.itemStatus || 'Pending'
  const { displayEddOrderDetailsPage = false } = config || {}

  const {
    title, messaging, showReturn, showTrack,
  } = getTitleAndMessage(product, itemStatus, displayEddOrderDetailsPage)
  const label = useMemo(() => <Text size="small">Return Item</Text>, [])

  const returnStatusData = useMemo(() => {
    const { merchantOrderId, emailAddress, orderId } = orderDetails
    let messageData = ''

    if (itemDetails.returnRequest && itemDetails.returnRequest.length === 0) return null

    const {
      // eslint-disable-next-line camelcase
      credit_status_code,
      order_item_id,
      credit_id,
      credit_status,
      rma_id,
      return_request_id,
      return_request_status,
    } = itemDetails.returnRequest || {}

    if (credit_status) {
      messageData = creditStatusMapping(credit_status_code, creditStatus)
    }

    // Priority: Display RMA ID
    if (typeof itemDetails.returnRequest !== 'undefined' &&
      itemDetails.returnRequest.rma_id) {
      return {
        message: messageData.main_reason || null,
        idType: 'RMA ID:',
        rmaId: rma_id,
        returnRequestId: return_request_id || null,
        orderItemId: order_item_id,
        orderId: orderDetails.orderId,
        merchantOrderId,
        email: emailAddress,
        creditId: credit_id,
      }
    }

    if (credit_status) {
      messageData = creditStatusMapping(credit_status_code, creditStatus)

      return {
        message: messageData.main_reason || null,
        idType: 'Credit ID:',
        rmaId: rma_id,
        orderItemId: order_item_id,
        orderId,
        merchantOrderId,
        email: emailAddress,
        creditId: credit_id,
      }
    }

    // Display Return Request ID
    if (typeof itemDetails.returnRequest !== 'undefined' && itemDetails.returnRequest.return_request_id !== '') {
      messageData = returnRequestStatusMapping(return_request_status, returnRequestStatus)

      return {
        message: messageData.main_reason || null,
        idType: 'Return Request ID:',
        returnRequestId: return_request_id,
        rmaId: rma_id || null,
        merchantOrderId,
        email: emailAddress,
        orderId: orderDetails.orderId,
        orderItemId: order_item_id,
      }
    }

    return null
  }, [product])

  const isLifetimeReplacement = useGetLifetimeReplacementFlag()

  const buttonData = useMemo(() => ({
    itemDetails,
    showReturn,
    navigateToRMA,
    partNumber: product?.partNumber,
    orderId,
    type: isLifetimeReplacement ? getButtonType() : '',
  }), [
    itemDetails,
    product,
    orderId,
    navigateToRMA,
    showReturn,
    isLifetimeReplacement,
  ])

  const OpenChatBox = useCallback(e => {
    e.preventDefault()
    OpenChatHandler()
  }, [])


  return (
    <>
      <Container>
        <ResponsiveContainer wrap>
          <OrderItemBox>
            <OrderDetailsBox>
              <ProductImage product={product} />

              <OrderDetailsTextBox>
                <ProductInformation vehicle={vehicle} product={product} core={core} formatBrand={formatBrand} />
              </OrderDetailsTextBox>
            </OrderDetailsBox>
            <KitContainer
              alignSelf="end"
            >
              <SetKitDisplay
                parentSku={product.parentSku}
                style={{ margin: '8pt 4px', borderRadius: '4pt' }}
              />
              {!returnStatusData && (
                <Box
                  background="#FFFEF0"
                  pad={{ vertical: 'small', horizontal: 'medium' }}
                >
                  {title && (
                    <Box direction="row">
                      <Text size="small" color="dark-2">
                        <Text size="small" color="dark-2" weight="bold">
                          {title}
                        </Text>
                      </Text>
                    </Box>
                  )}
                  {messaging && (
                    <Box>
                      {itemStatus === 'Cancelled' ? (
                        <Text size="xsmall" color="dark-2">
                          {'This order is either cancelled or out of stock. If you have any questions or concerns, you can contact us through our '}
                          <LiveChat
                            onClick={OpenChatBox}
                            href="#"
                            rel="noreferrer"
                          >
                            Live Chat
                          </LiveChat>
                          . Our chat agents are available Mon-Sun from 4 AM to 8 PM PST.
                        </Text>
                      ) : (
                        <Text size="xsmall" color="dark-2">
                          {messaging}
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {returnStatusData && (
                <ReturnStatus
                  returnStatusData={returnStatusData}
                  orderDetails={orderDetails}
                  creditStatus={creditStatus}
                  returnRequestStatus={returnRequestStatus}
                  setReturnDetailsData={setReturnDetailsData}
                  setDisplayReturnDetails={setDisplayReturnDetails}
                  isDesktop={isDesktop}
                />
              )}
            </KitContainer>
          </OrderItemBox>

          <ActionContainer
            gap="small"
          >
            <Box width="100%">
              <ActionTrackContainer>
                {showTrack && (
                  <Anchor
                    href={`${product.trackingUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OrderDetailsBlueButton
                      custom
                      hideBorder
                      label={<Text size="small">Track Item</Text>}
                    />
                  </Anchor>
                )}
              </ActionTrackContainer>
              <BuyItAgainButton productUri={product?.productUri} itemStatus={itemStatus} />
              <OrderDetailItemButton data={buttonData} />
            </Box>
          </ActionContainer>
        </ResponsiveContainer>
      </Container>
    </>
  )
}
